import {action, makeObservable, observable} from 'mobx';
import {ApiRepository, ValidationError} from '../base/api-repository';
import {UserStore} from '../user/user-store';
import {LabelStore, Language} from '../base/label-store';
import {ViewStore} from '../base/view-store';
import {isDateInFutureForModal, isEvenDate} from '../base/dateUtils';

export type QuestionsTab = 'catalog-questions' | 'ad-hoc-questions' | 'announcements' | 'app-level-announcements';
export type QuestionsModal = 'add-adhoc-question' | 'edit-adhoc-question' | 'add-catalog-question' | 'edit-catalog-question' | 'add-custom-category' | 'edit-custom-category' | 'question-help' | 'disable-category' | 'disable-question' | 'add-announcement' | 'edit-announcement' | 'add-app-level-announcement' | 'edit-app-level-announcement';

export class QuestionsStore extends ViewStore {
    @observable
    payload?: AllQuestionsPayload = undefined;
    @observable
    categories?: Category[] = undefined;
    @observable
    variables: QuestionsVariables = new QuestionsVariables();

    constructor(
        private api: ApiRepository,
        private userStore: UserStore,
        labelStore: LabelStore) {
        super(labelStore);
        this.variables.openTab = 'catalog-questions';
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.load(async () => {
            this.payload = await this.api.get<AllQuestionsPayload>(`/questions/employee/${this.userStore.payload!.employeeId}`);
            this.categories = await this.api.get<Category[]>(`/categories/employee/${this.userStore.payload!.employeeId}`);
        });
    }

    @action
    onQuestionsTabChange(context: QuestionsTab): void {
        this.variables.openTab = context;
    }

    @action
    openAddAdHocQuestionModal(): void {
        this.resetErrors();
        this.variables.adHocQuestionInEditing = undefined;
        this.variables.openModal = 'add-adhoc-question';
    }

    @action
    openEditAdHocQuestionModal(adHocQuestion: SingleAdHocQuestion): void {
        this.resetErrors();
        this.variables.adHocQuestionInEditing = adHocQuestion;
        this.variables.openModal = 'edit-adhoc-question';
    }

    @action
    openAddAnnouncementModal(): void {
        this.resetErrors();
        this.variables.announcementInEditing = undefined;
        this.variables.openModal = 'add-announcement';
    }

    @action
    openEditAnnouncementModal(announcement: Announcement): void {
        this.resetErrors();
        this.variables.announcementInEditing = announcement;
        this.variables.openModal = 'edit-announcement';
    }

    @action
    openAddCatalogQuestionModal(): void {
        this.resetErrors();
        this.variables.catalogQuestionInEditing = undefined;
        this.variables.openModal = 'add-catalog-question';
    }

    @action
    openAddCustomQuestionCategoryModal(): void {
        this.resetErrors();
        this.variables.openModal = 'add-custom-category';
    }

    @action
    openEditCatalogQuestionModal(catalogQuestion: SingleCatalogQuestion, category: string): void {
        this.resetErrors();
        this.variables.catalogQuestionInEditing = catalogQuestion;
        this.variables.catalogQuestionCategory = category;
        this.variables.openModal = 'edit-catalog-question';
    }

    @action
    openQuestionsPageHelpModal(): void {
        this.variables.openModal = 'question-help';
    }

    @action
    openEditCustomCategoryModal(category: Category): void {
        this.resetErrors();
        this.variables.customCategory = category;
        this.variables.openModal = 'edit-custom-category';
    }

    @action
    openDisableCategoryForBusinessUnitModal(category: string): void {
        this.resetErrors();
        this.variables.customCategoryIdInEditing = category;
        this.variables.openModal = 'disable-category';
    }

    @action
    openDisableQuestionForBusinessUnitModal(catalogQuestion: SingleCatalogQuestion): void {
        this.resetErrors();
        this.variables.catalogQuestionIdInEditing = catalogQuestion.id;
        this.variables.catalogQuestionInEditing = catalogQuestion;
        this.variables.openModal = 'disable-question';
    }

    @action
    saveCatalogQuestion(catalogQuestion: CatalogQuestionToSave): void {
        this.refresh(async () => {
            this.resetErrors();
            if (this.areValidLanguages(catalogQuestion.questionLanguages)) {
                try {
                    if (this.variables.openModal === 'add-catalog-question') {
                        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/add-catalog-question`, catalogQuestion);
                    } else {
                        catalogQuestion.id = this.variables.catalogQuestionInEditing?.id;
                        if (this.variables.catalogQuestionInEditing?.isAsked === true) {
                            if (window.confirm(this.label('questions.catalog-question.edit-confirm'))) {
                                await this.saveEditedCatalogQuestion(catalogQuestion);
                            }
                        } else {
                            await this.saveEditedCatalogQuestion(catalogQuestion);
                        }
                    }
                    this.closeModal();
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        this.variables.shouldShowValidation = true;
                        this.variables.validationReason = (e as ValidationError).errorMessageCode;
                    } else {
                        this.closeModal();
                        throw e;
                    }
                }
            } else {
                this.variables.shouldShowValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    saveCustomCategory(customCategory: CustomCategoryToSave | CustomCategoryToEdit): void {
        this.refresh(async () => {
            this.resetErrors();

            if (!this.areValidLanguages(customCategory.categoryLanguages)) {
                this.variables.shouldShowValidation = true;

                return;
            }

            try {
                const { openModal } = this.variables;
                const employeeId = this.userStore.payload!.employeeId;

                if (openModal === 'add-custom-category') {
                    await this.api.post(`/categories/employee/${employeeId}/add-custom-category`, customCategory);
                } else if (openModal === 'edit-custom-category') {
                    await this.api.post(`/categories/employee/${employeeId}/edit-custom-category`, customCategory);
                }

                this.closeModal();
            } catch (e: unknown) {
                if ((e as ValidationError).name === 'ValidationError') {
                    this.variables.shouldShowValidation = true;
                    this.variables.validationReason = (e as ValidationError).errorMessageCode;
                } else {
                    this.closeModal();
                    throw e;
                }
            }
            await this.loadPayload();
        });
    }

    @action
    deleteCustomCategory(customCategoryId: string): void {
        this.refresh(async () => {
            await this.api.post(`/categories/employee/${this.userStore.payload!.employeeId}/delete-custom-category/${customCategoryId}`);
            await this.loadPayload();
        });
    }

    @action
    saveAdHocQuestion(adHocQuestion: AdHocQuestionToSave): void {
        this.refresh(async () => {
            this.resetErrors();
            if (this.isValidAdhocQuestion(adHocQuestion)) {
                try {
                    if (this.variables.openModal === 'add-adhoc-question') {
                        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/add-ad-hoc-question`, adHocQuestion);
                    } else {
                        adHocQuestion.id = this.variables.adHocQuestionInEditing?.id;
                        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/edit-ad-hoc-question`, adHocQuestion);
                    }
                    this.closeModal();
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        this.variables.shouldShowValidation = true;
                        this.variables.validationReason = (e as ValidationError).errorMessageCode;
                    } else {
                        this.closeModal();
                        throw e;
                    }
                }
            } else {
                this.variables.shouldShowValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    saveAnnouncement(announcement: Announcement): void {
        this.refresh(async () => {
            this.resetErrors();
            if (this.areValidLanguages(announcement.languages)) {
                try {
                    if (this.variables.openModal === 'add-announcement') {
                        await this.api.post(`/announcements/employee/${this.userStore.payload!.employeeId}/add-announcement`, announcement);
                    } else {
                        announcement.id = this.variables.announcementInEditing?.id;
                        await this.api.post(`/announcements/employee/${this.userStore.payload!.employeeId}/edit-announcement`, announcement);
                    }
                    this.closeModal();
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        this.variables.shouldShowValidation = true;
                        this.variables.validationReason = (e as ValidationError).errorMessageCode;
                    } else {
                        this.closeModal();
                        throw e;
                    }
                }
            } else {
                this.variables.shouldShowValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    closeModal(): void {
        this.variables.openModal = undefined;
    }

    @action
    deleteAdHocQuestion(adHocQuestion: QuestionToDelete): void {
        this.refresh(async () => {
            await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/delete-ad-hoc-question`, adHocQuestion);
            await this.loadPayload();
        });
    }

    @action
    deleteAnnouncement(announcementToDeleteId: string): void {
        const announcementToDelete: AnnouncementToDelete = {
            id: announcementToDeleteId,
        };

        this.refresh(async () => {
            await this.api.post(`/announcements/employee/${this.userStore.payload!.employeeId}/delete-announcement`, announcementToDelete);
            await this.loadPayload();
        });
    }

    @action
    deleteCatalogQuestion(catalogQuestion: QuestionToDelete): void {
        this.refresh(async () => {
            await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/delete-catalog-question`, catalogQuestion);
            await this.loadPayload();
        });
    }

    @action
    async onSendingActiveToggle(questionId: string, requestedIsActive: boolean): Promise<void> {
        const isActiveCatalogQuestion: IsActiveCatalogQuestion = {
            catalogQuestionId: questionId,
            requestedIsActive,
        };

        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/toggle-question`, isActiveCatalogQuestion);
        await this.loadPayload();
    }

    @action
    async onSendingActiveCategoryToggle(categoryId: string, isActive: boolean): Promise<void> {
        const isActiveQuestionCategory: IsActiveQuestionCategory = {
            categoryId,
            isActive,
        };

        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/toggle-category`, isActiveQuestionCategory);
        await this.loadPayload();
    }

    @action
    async disableQuestionForBusinessUnit(businessUnitIds: string[] | undefined, questionId: string, disabled: boolean): Promise<void> {
        const disabledQuestionForBusinessUnit: DisabledQuestion = {
            businessUnitIds: businessUnitIds || [],
            questionId,
            disabled,
        };

        if (businessUnitIds?.length === 0) {
            this.closeModal();
        } else {

            await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/disable-question`, disabledQuestionForBusinessUnit);
            await this.loadPayload();
            this.closeModal();
        }
    }

    @action
    async disableCategoryForBusinessUnit(businessUnitIds: string[] | undefined, categoryId: string, disabled: boolean): Promise<void> {
        const disabledCategoryForBusinessUnit: DisabledCategory = {
            businessUnitIds: businessUnitIds || [],
            categoryId,
            disabled,
        };

        if (businessUnitIds?.length === 0) {
            this.closeModal();
        } else {
            await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/disable-category`, disabledCategoryForBusinessUnit);
            await this.loadPayload();
        }
    }

    @action
    async translateText(fromLanguage: string, text: string, toLanguages: string[]): Promise<Record<string, string>> {
        if (!text) {
            return {};
        }

        try {
            return await this.api.post('/translation/questions', {
                fromLanguage,
                toLanguages,
                text,
            });
        } catch (e: unknown) {
            if ((e as ValidationError).name === 'ValidationError') {
                this.variables.shouldShowValidation = true;
                this.variables.validationReason = (e as ValidationError).errorMessageCode;
            } else {
                this.closeModal();
                throw e;
            }

            return {};
        }
    }

    @action
    async translateTextForLanguages(
        fromLanguage: string,
        text: string,
        companyLanguages: string[],
        languageMap: Record<string, { setter: (value: string) => void }>,
    ): Promise<void> {
        if (!text) {
            return;
        }

        try {
            const toLanguages = companyLanguages.filter((language) => language !== fromLanguage);
            const response = await this.translateText(fromLanguage, text, toLanguages);
            const translations = response as Record<string, string>;

            toLanguages.forEach((lang) => {
                const translatedText = translations[lang.toUpperCase()];

                if (translatedText && languageMap[lang]) {
                    languageMap[lang].setter(translatedText);
                }
            });
        } catch (error) {
            console.error('Translation failed:', error);
        }
    }

    isValidAdhocQuestion(questionToSave: AdHocQuestionToSave): boolean {
        if (!this.areValidLanguages(questionToSave.questionLanguages)) {
            return false;
        }

        return !(!questionToSave.sendDate || this.checkSendDate(new Date(questionToSave.sendDate)));
    }

    areValidLanguages(questionToSaveLanguages: Languages): boolean {
        if (this.checkText(questionToSaveLanguages.textEnglish, 'en')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textEstonian, 'et')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textFinnish, 'fi')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textLatvian, 'lv')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textLithuanian, 'lt')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textSwedish, 'sv')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textRussian, 'ru')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textPolish, 'pl')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textJapanese, 'ja')) {
            return false;
        }
        if (this.checkText(questionToSaveLanguages.textVietnamese, 'vi')) {
            return false;
        }

        return true;
    }

    checkSendDate(value: Date | undefined): undefined | 'missing' | 'not-in-future' | 'not-even-date'{
        if (value === undefined) {
            return 'missing';
        } else if (!isDateInFutureForModal(value)) {
            return 'not-in-future';
        } else if (this.userStore.payload?.companyQuestionsFrequency === 'one_on_even_dates' && !isEvenDate(value)){
            return 'not-even-date';
        } else {
            return undefined;
        }
    }

    checkAnnouncementDate(value: Date | undefined): undefined | 'missing' | 'not-in-future' | 'not-even-date'{
        if (value === undefined) {
            return 'missing';
        } else {
            return undefined;
        }
    }

    checkText(value: string | undefined, language: Language): undefined | 'missing' | `forbidden-characters` {
        const companyLanguage = this.payload!.companyDefaultLanguage;

        if (companyLanguage === language && value === undefined) {

            return 'missing';
        }
        if (companyLanguage === language && value === '') {

            return 'missing';
        }
        if (this.variables.validationReason === `question_text_forbidden_${language}`) {

            return 'forbidden-characters';
        }

        return undefined;
    }

    private async saveEditedCatalogQuestion(catalogQuestion: CatalogQuestionToSave): Promise<void> {
        await this.api.post(`/questions/employee/${this.userStore.payload!.employeeId}/edit-catalog-question`, catalogQuestion);
    }

    private async loadPayload(): Promise<void> {
        this.payload = await this.api.get<AllQuestionsPayload>(`/questions/employee/${this.userStore.payload!.employeeId}`);
        this.categories = await this.api.get<Category[]>(`/categories/employee/${this.userStore.payload!.employeeId}`);
    }

    private resetErrors(): void {
        this.variables.shouldShowValidation = false;
        this.variables.validationReason = undefined;
    }
}

interface AllQuestionsPayload {
    allQuestionsWithCategories: CatalogQuestionsWithCategory[];
    allAdHocQuestions: SingleAdHocQuestion[];
    allAnnouncements: Announcement[];
    rootBusinessUnitId: string;
    allCompanySingleBusinessUnits: SingleBusinessUnit[],
    employeeLocations: string[],
    employeeSegments: string[],
    targetRoles: string[],
    questionCategories: Category[],
    companyDefaultLanguage: Language,
}

export interface SingleBusinessUnit {
    id: string;
    businessUnitName: string;
}

export interface CatalogQuestionsWithCategory {
    categoryId: string;
    createdTs: Date;
    allCatalogQuestions: SingleCatalogQuestion[];
    disabledBusinessUnits: string[];
    isOnboarding: boolean;
}

export interface SingleCatalogQuestion {
    id: string;
    questionLanguages: Languages;
    isActive: boolean;
    requestedIsActive?: boolean;
    isAsked: boolean;
    isCustomCatalogQuestion: boolean;
    categoryId: string;
    disabledBusinessUnits: string[]
}

export interface CatalogQuestionToSave {
    category?: string
    id?: string;
    questionLanguages: Languages;
    isActive: boolean;
    categoryId?: string;
}

export interface CustomCategoryToSave {
    categoryLanguages: Languages,
    isOnboarding: boolean,
}

export interface CustomCategoryToEdit {
    categoryId: string,
    isOnboarding: boolean,
    categoryLanguages: Languages,
    disabledCategoryForBusinessUnit?: DisabledCategoryForBusinessUnit
}

export interface DisabledCategoryForBusinessUnit {
    businessUnitIds: string[],
    categoryId: string,
}

export interface Category {
    id: string;
    isOnboarding: boolean;
    categoryName: string;
    textEnglish: string;
    textEstonian: string;
    textFinnish: string;
    textLatvian: string;
    textLithuanian: string;
    textPolish: string;
    textRussian: string;
    textSwedish: string;
    textJapanese: string;
    textVietnamese: string;
}

export interface IsActiveCatalogQuestion {
    catalogQuestionId: string;
    requestedIsActive: boolean
}

export interface IsActiveQuestionCategory {
    categoryId: string;
    isActive: boolean
}

export interface DisabledQuestion {
    businessUnitIds: string[];
    questionId: string;
    disabled: boolean;
}

export interface DisabledCategory {
    businessUnitIds: string[];
    categoryId: string;
    disabled: boolean;
}

export interface SingleAdHocQuestion {
    id: string;
    questionLanguages: Languages;
    sendDate: string;
    businessUnitId: string;
    businessUnitName: string;
    employeeSegment: string;
    employeeLocation: string;
    onlyFreetextAnswer: boolean;
}

export interface Announcement {
    id?: string;
    languages: Languages;
    startAt: string;
    endAt: string;
    targetRoles: string[];
    isAppLevel: boolean;
}

export interface AdHocQuestionToSave {
    id?: string;
    questionLanguages: Languages;
    sendDate?: string;
    businessUnitId: string;
    employeeSegment: string;
    employeeLocation: string;
    onlyFreetextAnswer: boolean;
}

export interface QuestionToDelete {
    id: string;
    businessUnitId?: string;
}

export interface AnnouncementToDelete {
    id: string;
}

export interface Languages {
    textEnglish?: string;
    textEstonian?: string;
    textFinnish?: string;
    textLatvian?: string;
    textLithuanian?: string;
    textPolish?: string;
    textSwedish?: string;
    textRussian?: string;
    textJapanese?: string;
    textVietnamese?: string;
}

class QuestionsVariables {
    @observable
    openTab?: QuestionsTab = undefined;
    @observable
    openModal?: QuestionsModal = undefined;
    @observable
    adHocQuestionInEditing?: SingleAdHocQuestion = undefined;
    @observable
    catalogQuestionInEditing?: SingleCatalogQuestion = undefined;
    @observable
    announcementInEditing?: Announcement = undefined;
    @observable
    catalogQuestionIdInEditing?: string = undefined;
    @observable
    customCategory?: Category = undefined;
    @observable
    catalogQuestionCategory?: string = undefined;
    @observable
    shouldShowValidation?: boolean = false;
    @observable
    validationReason?: string = undefined;
    @observable
    customCategoryInEditing?: CustomCategoryToEdit = undefined;
    @observable
    customCategoryIdInEditing?: string = undefined;

    constructor() {
        makeObservable(this);
    }
}
